"use client";

import SelectProvince from "@/components/profile/selectProvince";
import IranLogo from "@/helpers/svg/iranLogo.svg";
import React, { useState, useMemo, useEffect, useCallback } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { anavatanCities } from "@/config/enums/anavatanCities.enum";
import { EffectFade } from "swiper/modules";
import { showCaseComponentSwitch } from "@/components/showcase/showcase.utils";
import "swiper/css";
import "swiper/css/effect-fade";
import axiosInstance from "@/config/axiosInstance";
import { getCMSApiRoute } from "@/helpers/api.util";
import Cards from "@/components/content/cards";
import { Card, CardBody, Spinner } from "@nextui-org/react";

import classNames from "classnames";

// Helper components for better code organization
const LoadingSpinner = () => (
  <div className="flex justify-center items-center w-full min-h-[20vh]">
    <Spinner size="lg" />
  </div>
);

const ShowcaseContent = ({ anavatanData }) => (
  <>{anavatanData?.map((item, index) => showCaseComponentSwitch(item, "", index))}</>
);

const CityContent = ({ cityData }) => (
  <div className="p-4">
    {cityData?.map((item, index) => (
      <div key={`${item.name}-${index}`} className="mb-10 max-w-[1920px] mx-auto">
        <h3 className="text-3xl font-bold py-4">{item.name}</h3>
        <Cards
          items={item.contents}
          isSwiper={true}
          categoryName={item.name}
          showTitle={false}
          type="series"
          isArtist={false}
          noMX={true}
          noContainer={true}
        />
      </div>
    ))}
  </div>
);

const NoContentMessage = () => (
  <div className="flex justify-center items-center w-full min-h-[20vh]">
    <Card>
      <CardBody>محتوایی یافت نشد.</CardBody>
    </Card>
  </div>
);

const AnavatanScreen = ({ showcaseData }) => {
  const [instance, setInstance] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [cityData, setCityData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchCityData = async () => {
    if (selectedCity?.id === 0) return;
    if (loading) return;
    setLoading(true);

    try {
      const response = await axiosInstance.get(
        getCMSApiRoute("province-content"),
        {
          params: {
            province: decodeURIComponent(selectedCity?.key),
          },
        }
      );

      const data = response.data?.data;
      if (!data) return;
      const processedData = data
        ?.filter((city) => city.contents?.length > 0)
        ?.map((city) => ({
          ...city,
          contents: city.contents.map((content) => ({ content })),
        }));

      setCityData(processedData);
    } catch (error) {
      console.error("Error fetching city data:", error);
      setCityData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(
    () => fetchCityData,
    [selectedCity?.id, selectedCity?.key, loading]
  );

  const anavatanData = useMemo(() => {
    if (!showcaseData) return [];

    const results = Object.values(showcaseData)
      .flat()
      .sort((a, b) => (a.priority || 0) - (b.priority || 0));

    return results;
  }, [showcaseData]);

  return (
    <div className="h-auto w-full pb-24">
      <div className="h-[45vh] md:h-[50vh] lg:h-[65vh] w-full relative">
        <Swiper
          onInit={(swiper) => setInstance(swiper)}
          className="w-full h-full z-0"
          modules={[EffectFade]}
          effect="fade"
          slidesPerView={1}
          initialSlide={8}
        >
          {anavatanCities?.map((item, index) => (
            <SwiperSlide className="bg-black" key={item.id}>
              <div className="relative w-full h-full">
                <img
                  src={item.bg}
                  alt={item.name}
                  className={classNames(
                    "object-cover object-center",
                    item.className
                  )}
                  style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
        <div className="absolute left-0 right-0 bottom-0 top-0 flex justify-center items-end z-[1]">
          <div className="relative w-full h-1/2 z-10">
            <img
              src="/options-shadow.png"
              alt="vertical shadow"
              className="object-cover z-10"
              style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
            />
          </div>
        </div>
        <div className="absolute top-0 left-0 right-0 bottom-0 flex justify-center items-end z-10">
          <div className="z-10 flex justify-start items-center lg:mr-12 xs:mr-6 mb-6 md:container w-full gap-4">
            <IranLogo />
            <h2 className="font-medium sm:font-bold md:font-extrabold lg:font-black text-[#F0F0F0] text-3xl md:text-4xl lg:text-5xl">
              آناوطن
            </h2>
          </div>
        </div>
      </div>
      <SelectProvince instance={instance} setSelectedCity={setSelectedCity} />
      <div className="w-full lg:mt-10 min-h-[20vh]">
        {/* {loading ? (
          <LoadingSpinner />
        ) : (anavatanData?.length > 0 && !selectedCity) ||
          selectedCity?.id === 0 ? (
          <ShowcaseContent anavatanData={anavatanData} />
        ) : cityData && cityData?.length > 0 ? (
          <CityContent cityData={cityData} />
        ) : (
          <NoContentMessage />
        )} */}
        <div className="flex flex-col justify-center items-center w-full min-h-[20vh]">
          <h4 className="text-2xl font-medium">به زودی ...</h4>
          <p>بخش آناوطن به زودی در دسترس خواهد بود</p>
        </div>
      </div>
    </div>
  );
};

export default AnavatanScreen;
